import React, { useEffect, useRef, useState } from "react";
import { Form, Spin, Divider, Tag } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { save } from "../../../features/save/saveSlice";
import BreadCrumb from "../../../layout/BreadCrumb";
import { fetchData, generateReport } from "../../../features/fetch/fetchSlice";
import dayjs from "dayjs";
import ProgressToDateTable from "./ProgressToDateTable";
import { useWatch } from "antd/es/form/Form";
import MonthlyImages from "./MonthlyImages";
import BasicDataSection from "./sections/BasicDataSection";
import ContractInfoSection from "./sections/ContractInfoSection";
import ProjectProgressSection from "./sections/ProjectProgressSection";
import NarrativeSection from "./sections/NarrativeSection";

const MonthlyAdd = () => {
  const urlProjects = "main_project/fetch_mainprojects";
  const urlComponent = "project_component/fetch_projectcomponents";
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const { saving } = useSelector((state) => state.save);
  const { activeObjMontProj, dataProjects, dataComponent } = useSelector(
    (state) => ({
      activeObjMontProj: state.obj.activeObj["monthlyProj"],
      dataProjects: state.fetch.data[urlProjects] || [],
      dataComponent: state.fetch.data[urlComponent] || [],
    }),
  );

  const { counties } = useSelector((state) => state.fetch);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  async function handleCancel() {
    await navigate("/rob/monthly-report");
    await setDownload(false);
  }

  const [download, setDownload] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const handleDownload = async (item) => { 
    setReportLoading(true);
    const res = await dispatch(
      generateReport({
        url: "api/v1/reports/directReport",
        reportName: "PROJECT_UPDATE_REPORT",
        itemId: item,
        reportFormat: "PDF",
      }),
    );
    if (res?.payload) {
      const blobData = res.payload.data;
      const headers = res.payload.headers;

      const contentType = headers["content-type"] || "application/octet-stream";
      const contentDisposition = headers["content-disposition"];

      let fileName = "report";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          fileName = match[1];
        }
      }

      const blob = new Blob([blobData], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      setReportLoading(false);

      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      await toast.success(`${fileName} downloaded successfully`);
      await form.resetFields();
      await handleCancel();
    } else {
      toast.error("Failed to download file");
    }
  }; 
  const onFinish = async (data) => {
    data.mrId = activeObjMontProj?.mrId ? activeObjMontProj?.mrId : null;

    const images = Array.isArray(data.mrImages) ? data.mrImages : [];
    if (images.length < 4) {
      toast.error("Please upload at least 4 images before saving.");
      return;
    }
    data.mrImages = images;

    const res = await dispatch(
      save({
        ...data,
        url: "monthly_report/save_monthlyreport",
      }),
    );
    if (res?.payload?.success) {
      if (download) {
        await handleDownload(res?.payload?.result ?? activeObjMontProj?.mrId);
      }
      await toast.success(res?.payload?.message);
      await form.resetFields();
      await handleCancel();
    } else {
      toast.error(res?.payload?.message);
    }
  };

  const loadProjects = (projectName) =>
    dispatch(
      fetchData({
        url: urlProjects,
        start: 0,
        limit: null,
        projectName,
        projectStatus: null,
      }),
    );

  const initialFormValues = {
    ...activeObjMontProj,
    mrImages: activeObjMontProj?.mrImages || [],
    mrContractAwardDate: activeObjMontProj?.mrContractAwardDate
      ? dayjs(activeObjMontProj.mrContractAwardDate)
      : null,
    mrAmountPaidAdvanceDate: activeObjMontProj?.mrAmountPaidAdvanceDate
      ? dayjs(activeObjMontProj.mrAmountPaidAdvanceDate)
      : null,
    mrCommencementDate: activeObjMontProj?.mrCommencementDate
      ? dayjs(activeObjMontProj.mrCommencementDate)
      : null,
    mrExpectedCompletionDate: activeObjMontProj?.mrExpectedCompletionDate
      ? dayjs(activeObjMontProj.mrExpectedCompletionDate)
      : null,
    mrEvalDate: activeObjMontProj?.mrEvalDate
      ? dayjs(activeObjMontProj.mrEvalDate)
      : null,
  };

  useEffect(() => {
    form.setFieldsValue(initialFormValues);
  }, [activeObjMontProj, form]);

  const [selectedProjId, setSelectedProjId] = useState();

  const [dlp, setDlp] = useState(null);
  // The project's revised completion date, shown read-only (from the project,
  // like the DLP). Text from rtsa, so it is not shifted a day by time zones.
  const [revisedEndDate, setRevisedEndDate] = useState(null);
  const dlpFromProject = (project) =>
    project
      ? {
          has: project?.projectHasDlp ?? null,
          start: project?.projectDlpStartDate ?? null,
          end: project?.projectDlpEndDate ?? null,
          days: project?.projectDefectLiabilityPeriod ?? null,
        }
      : null;

  async function fetchProjectDlp(projectId) {
    const res = await dispatch(
      fetchData({
        url: urlProjects,
        projProjectId: projectId,
        limit: 1,
        start: 0,
      }),
    );
    setDlp(dlpFromProject(res?.payload?.data?.[0]));
    setRevisedEndDate(res?.payload?.data?.[0]?.projectRevisedEndDateText ?? null);
  }

  const urlPayments = `${process.env.REACT_APP_API_BASE_URL}/api/v2/mainProject/disbursement/get`;
  const [payments, setPayments] = useState([]);
  const pendingCount = (rows) =>
    rows.filter((p) => p?.pdStatus?.toUpperCase() === "PENDING_PAYMENT").length;
  // Advance recovered so far: the advance repayment deducted on each payment.
  const advanceRecovered = payments.reduce(
    (sum, p) => sum + (Number(p?.pdAdvancedRepaymentAmount) || 0),
    0,
  );

  async function fetchPayments(projectId) {
    if (!projectId) {
      setPayments([]);
      return;
    }
    const res = await dispatch(
      fetchData({
        url: urlPayments,
        pdProjectId: projectId,
        limit: 1000,
        start: 0,
      }),
    );
    const rows = Array.isArray(res?.payload?.data) ? res.payload.data : [];
    setPayments(rows);
    form.setFieldsValue({
      mrRaisedIpcs: rows.length,
      mrPendingIpcs: pendingCount(rows),
    });
  }

  const paymentColumns = [
    { title: "#", key: "index", render: (_, __, index) => index + 1 },
    { title: "Payment Description", dataIndex: "pdDescription" },
    { title: "Currency", dataIndex: "pdCurrencyCode" },
    {
      title: "Amount",
      key: "amount",
      render: (item) => {
        const value = item?.pdConvertedAmount ?? item?.pdAmount;
        return value != null ? Number(value).toLocaleString() : "--";
      },
    },
    {
      title: "Advance Repayment",
      dataIndex: "pdAdvancedRepaymentAmount",
      render: (value) => (value != null ? Number(value).toLocaleString() : "--"),
    },
    {
      title: "Paid Date",
      dataIndex: "paidDate",
      render: (value) => (value ? dayjs(value).format("DD-MM-YYYY") : "--"),
    },
    {
      title: "Due Date",
      dataIndex: "pdDueDate",
      render: (value) => (value ? dayjs(value).format("DD-MM-YYYY") : "--"),
    },
    {
      title: "Status",
      dataIndex: "pdStatus",
      render: (value) =>
        value ? (
          <Tag color={value?.toUpperCase() === "PAID" ? "green" : "blue"}>
            {value.replace(/_/g, " ")}
          </Tag>
        ) : (
          "--"
        ),
    },
  ];

  useEffect(() => {
    if (activeObjMontProj?.mrProjectId) {
      fetchProjectDlp(activeObjMontProj.mrProjectId);
      fetchPayments(activeObjMontProj.mrProjectId);
    }
  }, [activeObjMontProj?.mrProjectId]);

  const handleProjectNumberChange = (value) => {
    const selectedProject = dataProjects?.find(
      (item) => item?.projectName === value,
    );

    setSelectedProjId(selectedProject?.projectId);
    setDlp(dlpFromProject(selectedProject));
    setRevisedEndDate(selectedProject?.projectRevisedEndDateText ?? null);
    fetchPayments(selectedProject?.projectId);
    if (selectedProject) {
      const {
        projectId,
        projectNumber,
        projectCountyId,
        projectContractor,
        projectCost,
        projectTotalDisbursement,
        projectContractAwardDate,
        projectHasAdvancePayment,
        projectAdvancedPayment,
        projectAdvancedPaymentDate,
        projectScope,
        projectStartDate,
        projectEndDate,
        projectCompletionRate,
      } = selectedProject;

      form.setFieldsValue({
        mrProjectId: projectId,
        mrProjectNo: projectNumber || "",
        mrCountyId: projectCountyId || "",
        mrContractorName: projectContractor || "",
        mrContractSum: projectCost || "",
        mrAmountPaid: projectTotalDisbursement || "",

        mrContractAwardDate: projectContractAwardDate
          ? dayjs(projectContractAwardDate)
          : null,
        mrAdvancePaymentAvailable: projectHasAdvancePayment || "",
        mrAmountPaidAdvance: projectAdvancedPayment || "",
        mrAmountPaidAdvanceDate: projectAdvancedPaymentDate
          ? dayjs(projectAdvancedPaymentDate)
          : null,
        mrScope: projectScope || "",
        mrCommencementDate: projectStartDate ? dayjs(projectStartDate) : null,
        mrExpectedCompletionDate: projectEndDate ? dayjs(projectEndDate) : null,
        mrPhysicalProgres: projectCompletionRate || "",
      });
    } else {
      form.resetFields();
    }
  };
  const mrContractSum = Form.useWatch("mrContractSum", form);
  const mrAmountPaid = Form.useWatch("mrAmountPaid", form);

  useEffect(() => {
    if (mrContractSum != null && mrAmountPaid != null) {
      const balance = Math.max(mrContractSum - mrAmountPaid, 0);
      form.setFieldsValue({ mrContractSumBalance: balance });
    } else {
      form.setFieldsValue({ mrContractSumBalance: null });
    }
  }, [mrContractSum, mrAmountPaid]);

  const mrAdvancePaymentAvailable = useWatch("mrAdvancePaymentAvailable", form);

  useEffect(() => {
    if (mrAdvancePaymentAvailable === "No") {
      form.setFieldsValue({
        schContactPerson: null,
        schContactPhone: null,
      });
    }
  }, [mrAdvancePaymentAvailable]);

  const commencementDate = useWatch("mrCommencementDate", form);
  const expectedCompletionDate = useWatch("mrExpectedCompletionDate", form);

  useEffect(() => {
    if (commencementDate) {
      const start = dayjs(commencementDate);
      const today = dayjs();
      const daysElapsed = today.diff(start, "day");
      form.setFieldsValue({
        mrDaysElapsed: `${daysElapsed}`,
      });

      if (expectedCompletionDate) {
        const end = dayjs(expectedCompletionDate);
        const totalDays = end.diff(start, "day");
        const percentage =
          totalDays > 0 ? ((daysElapsed / totalDays) * 100).toFixed(2) : 0;
        form.setFieldsValue({ mrPercentageElapsedDay: percentage });
      } else {
        form.setFieldsValue({ mrPercentageElapsedDay: null });
      }
    } else {
      form.setFieldsValue({
        mrDaysElapsed: null,
        mrPercentageElapsedDay: null,
      });
    }
  }, [commencementDate, expectedCompletionDate]);

  return (
    <div className="w-full ">
      <BreadCrumb
        breadcrumbs={[
          { title: "Monthly Project Reports", path: "/rob/monthly-report" },
          { title: "Monthly Project Form" },
        ]}
      />
      <div className="white_card mt-[2.06rem]">
        <div className="form-add-h mb-[1.25rem]">Monthly Project Report</div>
        <Divider />
        <div className="w-full mt-[1.75rem]">
          <Form
            layout="vertical"
            ref={formRef}
            name="control-ref"
            onFinish={onFinish}
            scrollToFirstError={{ behavior: "smooth", block: "center" }}
            className=""
            style={{
              maxWidth: "100%",
            }}
            form={form}
            initialValues={initialFormValues}
          >
            <BasicDataSection
              dataProjects={dataProjects}
              counties={counties}
              onProjectChange={handleProjectNumberChange}
              onLoadProjects={loadProjects}
            />

            <ContractInfoSection
              mrAdvancePaymentAvailable={mrAdvancePaymentAvailable}
              dlp={dlp}
              payments={payments}
              pendingCount={pendingCount}
              advanceRecovered={advanceRecovered}
              paymentColumns={paymentColumns}
            />

            <ProjectProgressSection revisedEndDate={revisedEndDate} />

            {selectedProjId && (
              <>
                {" "}
                <div className="custom-title !bg-white !p-0 mt-[1.25rem]">
                  Progress to date
                </div>{" "}
                <ProgressToDateTable selectedProjId={selectedProjId} />
              </>
            )}

            <NarrativeSection />


            <Form.Item name="mrImages">
              <MonthlyImages existingImages={activeObjMontProj?.mrImages} />
            </Form.Item>

            <div className="flex justify-between mt-[48px] ">
              <div className="justify-start"></div>
              <div className="justify-end flex items-center mb-[58px] gap-x-5">
                <div className="w-[150px]">
                  <button
                    type="button"
                    key="back"
                    onClick={handleCancel}
                    className="cstm-btn"
                  >
                    Cancel
                  </button>
                </div>
                <div className="w-[200px]">
                  <button
                    disabled={saving || reportLoading}
                    type="submit"
                    onClick={() => setDownload(true)}
                    className="cstm-btn"
                  >
                    {(saving || reportLoading) && download ? (
                      <Spin className="blu_spin" />
                    ) : (
                      "Generate Report"
                    )}
                  </button>
                </div>
                <div className="w-[150px]">
                  <button
                    disabled={saving || reportLoading}
                    key="submit"
                    type="submit"
                    className="cstm-btn-2"
                    onClick={() => setDownload(false)}
                  >
                    {(saving || reportLoading) && !download ? (
                      <Spin />
                    ) : activeObjMontProj?.mrId ? (
                      "Update"
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};
export default MonthlyAdd;
